import { Box, Card, HStack, Skeleton, Spinner, Stack, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useRef } from 'react';
import { ProductItem } from './ProductItem.tsx';
import { ProductFilterState, SortingState, useProductListing } from '../hooks/useProductListing.tsx';

type Props = {
  sortingState: SortingState;
  filterState: ProductFilterState;
  onProductViewOpen: (id: number) => void;
  onTotalRowsChange?: (total: number) => void;
};

const ProductCardSkeleton = () => (
  <Card.Root variant='outline'>
    <Card.Body p={{ base: '4', md: '5' }}>
      <HStack gap={{ base: '4', md: '5' }} width='full' align='center'>
        <Skeleton width={{ base: '20', md: '28' }} height={{ base: '20', md: '28' }} rounded='xl' flexShrink={0} />
        <Stack flex='1' gap='3'>
          <Skeleton height='5' width='40%' />
          <HStack gap='2'>
            <Skeleton height='5' width='16' rounded='md' />
            <Skeleton height='5' width='20' rounded='md' />
          </HStack>
          <Skeleton height='4' width='30%' />
        </Stack>
        <Skeleton height='10' width='32' rounded='md' display={{ base: 'none', md: 'block' }} />
      </HStack>
    </Card.Body>
  </Card.Root>
);

const ProductListSkeleton = () => (
  <Stack gap='4'>
    {Array.from({ length: 6 }).map((_, index) => (
      <ProductCardSkeleton key={index} />
    ))}
  </Stack>
);

export const ProductList = (props: Props) => {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const productQuery = useProductListing({
    sortingState: props.sortingState,
    filterState: props.filterState
  });

  const allProducts = useMemo(() => {
    return productQuery.data?.pages.flatMap((page) => page.PagedRows) ?? [];
  }, [productQuery.data?.pages]);

  const totalRows = productQuery.data?.pages[0]?.TotalRows ?? 0;

  useEffect(() => {
    props.onTotalRowsChange?.(totalRows);
  }, [totalRows, props.onTotalRowsChange]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && productQuery.hasNextPage && !productQuery.isFetchingNextPage) {
          productQuery.fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [productQuery.hasNextPage, productQuery.isFetchingNextPage, productQuery.fetchNextPage]);

  const isInitialLoading = productQuery.isLoading && !productQuery.data;

  return (
    <Box maxW='5xl' mx='auto'>
      {isInitialLoading ? (
        <ProductListSkeleton />
      ) : (
        <Stack gap='4'>
          {allProducts.map((item) => (
            <ProductItem key={item.Id} onClickOrder={() => props.onProductViewOpen(item.Id)} {...item} />
          ))}

          <Box ref={loadMoreRef} py='4' textAlign='center'>
            {productQuery.isFetchingNextPage && (
              <HStack justifyContent='center' gap='2'>
                <Spinner size='sm' />
                <Text color='gray.500' fontSize='sm'>
                  Loading more products...
                </Text>
              </HStack>
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};
